<template>
  <v-container fluid>
    <!-- Header & Stats -->
    <v-row>
      <v-col cols="12">
        <v-card class="pa-4">
          <v-card-title class="d-flex align-center justify-space-between flex-wrap">
            <div class="d-flex align-center">
              <v-icon left color="primary" size="28">mdi-publish</v-icon>
              <span class="text-h5 font-weight-medium">Publishing</span>
            </div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <v-chip color="orange" dark small>
                <v-icon left x-small>mdi-file-document-outline</v-icon>
                {{ totalCount }} Draft{{ totalCount !== 1 ? 's' : '' }}
              </v-chip>
              <v-btn color="primary" small :disabled="!selectedListings.length" @click="autoPrice" :loading="autoPricing">
                <v-icon left small>mdi-currency-usd</v-icon>
                Auto Price ({{ selectedListings.length }})
              </v-btn>
              <v-btn color="success" small :disabled="!selectedListings.length" @click="bulkPublishConfirm">
                <v-icon left small>mdi-publish</v-icon>
                Publish Selected ({{ selectedListings.length }})
              </v-btn>
            </div>
          </v-card-title>

          <v-card-subtitle class="mt-1">
            Unpublished draft listings — set prices, review, and publish to WooCommerce
          </v-card-subtitle>

          <v-divider class="my-3"></v-divider>

          <!-- Search bar -->
          <v-row class="px-4">
            <v-col cols="12" sm="6" md="4">
              <v-text-field v-model="searchQuery" label="Search by SKU, title, or artist" prepend-inner-icon="mdi-magnify"
                clearable dense outlined hide-details @input="debouncedFetch"></v-text-field>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Listings Table -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card>
          <v-data-table
            v-model="selectedListings"
            :headers="headers"
            :items="listings"
            :loading="loading"
            :server-items-length="totalCount"
            :options.sync="tableOptions"
            show-select
            item-key="_id"
            @click:row="openPricingDialog"
            class="elevation-1 draft-table"
            :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
          >
            <template v-slot:item.images="{ item }">
              <v-avatar size="48" rounded class="my-1">
                <v-img v-if="item.images && item.images.length"
                  :src="getImageUrl(item.images[0])" contain></v-img>
                <v-icon v-else color="grey lighten-1">mdi-image-off</v-icon>
              </v-avatar>
            </template>

            <template v-slot:item.sku="{ item }">
              <span class="font-weight-medium">CRC{{ item.sku }}{{ item.inventoryLocation || '' }}</span>
            </template>

            <template v-slot:item.title="{ item }">
              <div>
                <div class="font-weight-medium text-truncate" style="max-width: 280px;">{{ item.title }}</div>
                <div class="text-caption grey--text">{{ item.artist || '—' }}</div>
              </div>
            </template>

            <template v-slot:item.format="{ item }">
              <v-chip x-small outlined>{{ item.format || item.productType || '—' }}</v-chip>
            </template>

            <template v-slot:item.condition="{ item }">
              <span class="text-caption">{{ item.recordCondition || item.itemCondition || '—' }} / {{ item.coverCondition || '—' }}</span>
            </template>

            <template v-slot:item.listPrice="{ item }">
              <div v-if="editingPriceId === item._id" class="d-flex align-center">
                <v-text-field v-model.number="editingPriceValue" dense hide-details solo flat type="number" prefix="₹"
                  autofocus style="max-width: 80px; font-size: 12px;" class="inline-price-input"
                  @keyup.enter="saveInlinePrice(item)"
                  @keyup.esc="editingPriceId = null" @blur="saveInlinePrice(item)"></v-text-field>
              </div>
              <div v-else @click.stop="startInlineEdit(item)" style="cursor: pointer;" title="Click to edit price">
                <span v-if="item.listPrice" class="font-weight-bold green--text text--darken-2">₹{{ item.listPrice }}</span>
                <v-chip v-else x-small color="warning" dark>No price</v-chip>
              </div>
            </template>

            <template v-slot:item.listedOn="{ item }">
              <span class="text-caption">{{ formatDate(item.listedOn) }}</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon small color="primary" @click.stop="openPricingDialog(item)" title="Set Price & Review">
                <v-icon small>mdi-tag-outline</v-icon>
              </v-btn>
              <v-btn icon small color="success" @click.stop="publishSingle(item)" title="Publish Now" :loading="item._publishing">
                <v-icon small>mdi-publish</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pricing Dialog -->
    <v-dialog v-model="pricingDialog" max-width="750" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center primary white--text">
          <v-icon left dark>mdi-tag-outline</v-icon>
          Set Price — {{ dialogListing ? dialogListing.title : '' }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="pricingDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4" v-if="dialogListing">
          <!-- Product info row -->
          <v-row>
            <v-col cols="12" sm="3" class="text-center">
              <v-avatar size="100" rounded>
                <v-img v-if="dialogListing.images && dialogListing.images.length"
                  :src="getImageUrl(dialogListing.images[0])" contain></v-img>
                <v-icon v-else size="48" color="grey">mdi-image-off</v-icon>
              </v-avatar>
            </v-col>
            <v-col cols="12" sm="9">
              <div class="text-subtitle-1 font-weight-bold">{{ dialogListing.title }}</div>
              <div class="text-body-2 grey--text">{{ dialogListing.artist || 'Unknown Artist' }}</div>
              <v-chip-group class="mt-1">
                <v-chip x-small outlined>SKU: CRC{{ dialogListing.sku }}{{ dialogListing.inventoryLocation || '' }}</v-chip>
                <v-chip x-small outlined>{{ dialogListing.format || dialogListing.productType || '—' }}</v-chip>
                <v-chip x-small outlined>Rec: {{ dialogListing.recordCondition || dialogListing.itemCondition || '—' }}</v-chip>
                <v-chip x-small outlined>Cover: {{ dialogListing.coverCondition || '—' }}</v-chip>
              </v-chip-group>

              <v-row class="mt-3">
                <v-col cols="6">
                  <v-text-field v-model.number="dialogPrice" label="Selling Price (₹)" type="number" outlined dense
                    prefix="₹" hide-details="auto" :rules="[v => v > 0 || 'Price required']"></v-text-field>
                </v-col>
                <v-col cols="6" class="d-flex align-center">
                  <div v-if="dialogListing.buyingPrice" class="text-caption">
                    Buying: <strong>₹{{ dialogListing.buyingPrice }}</strong>
                    <span v-if="dialogPrice && dialogListing.buyingPrice" class="ml-2">
                      Margin: <strong :class="dialogPrice - dialogListing.buyingPrice > 0 ? 'green--text' : 'red--text'">
                        ₹{{ dialogPrice - dialogListing.buyingPrice }}
                        ({{ ((dialogPrice - dialogListing.buyingPrice) / dialogListing.buyingPrice * 100).toFixed(0) }}%)
                      </strong>
                    </span>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Similar Products -->
          <div class="d-flex align-center mb-2">
            <v-icon small color="orange" class="mr-1">mdi-compare-horizontal</v-icon>
            <span class="text-subtitle-2 font-weight-medium">Similar Published Products</span>
            <v-spacer></v-spacer>
            <v-btn x-small text color="primary" @click="fetchSimilar" :loading="loadingSimilar">
              <v-icon x-small left>mdi-refresh</v-icon> Refresh
            </v-btn>
          </div>

          <v-alert v-if="!loadingSimilar && similarProducts.length === 0" type="info" dense text class="text-caption">
            No similar published products found for this artist.
          </v-alert>

          <v-simple-table v-if="similarProducts.length" dense class="similar-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Format</th>
                <th>Condition</th>
                <th>Price</th>
                <th>Published</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sp in similarProducts" :key="sp._id">
                <td>
                  <v-avatar size="32" rounded>
                    <v-img v-if="sp.images && sp.images.length"
                      :src="getImageUrl(sp.images[0])" contain></v-img>
                  </v-avatar>
                </td>
                <td>
                  <div class="text-caption font-weight-medium">{{ sp.title }}</div>
                  <div class="text-caption grey--text">{{ sp.artist }}</div>
                </td>
                <td class="text-caption">{{ sp.format || '—' }}</td>
                <td class="text-caption">{{ sp.recordCondition || sp.itemCondition || '—' }} / {{ sp.coverCondition || '—' }}</td>
                <td>
                  <span class="font-weight-bold">₹{{ sp.listPrice || '—' }}</span>
                </td>
                <td class="text-caption">{{ formatDate(sp.publishedOn) }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-alert v-if="similarProducts.length" type="success" dense text class="mt-3 text-caption">
            Average price of similar products: <strong>₹{{ averageSimilarPrice }}</strong>
            <v-btn x-small text color="success" class="ml-2" @click="dialogPrice = averageSimilarPrice">
              Apply
            </v-btn>
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn text @click="pricingDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="savePriceOnly" :loading="savingPrice" :disabled="!dialogPrice">
            <v-icon left small>mdi-content-save</v-icon>
            Save Price (Keep Draft)
          </v-btn>
          <v-btn color="success" @click="savePriceAndPublish" :loading="publishing" :disabled="!dialogPrice">
            <v-icon left small>mdi-publish</v-icon>
            Save & Publish
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Auto Price Results Dialog -->
    <v-dialog v-model="autoPriceDialog" max-width="750" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center primary white--text">
          <v-icon left dark>mdi-currency-usd</v-icon>
          Set Prices — No Previous Listing Found
          <v-spacer></v-spacer>
          <v-btn icon dark @click="autoPriceDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="info" dense text class="mb-3 text-caption">
            These listings have no similar published products. Please enter prices manually.
          </v-alert>

          <v-simple-table dense>
            <thead>
              <tr>
                <th>Image</th>
                <th>SKU</th>
                <th>Title</th>
                <th>Format</th>
                <th>Condition</th>
                <th>Price (₹)</th>
                <th>Apply</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in autoPriceResults" :key="r._id">
                <td>
                  <v-avatar size="40" rounded class="my-1">
                    <v-img v-if="r.images && r.images.length"
                      :src="getImageUrl(r.images[0])" contain></v-img>
                    <v-icon v-else small>mdi-image-off</v-icon>
                  </v-avatar>
                </td>
                <td class="text-caption font-weight-medium">CRC{{ r.sku }}</td>
                <td>
                  <div class="text-caption font-weight-medium text-truncate" style="max-width: 160px;">{{ r.title }}</div>
                  <div class="text-caption grey--text">{{ r.artist }}</div>
                </td>
                <td class="text-caption">{{ r.format || '—' }}</td>
                <td class="text-caption">{{ r.recordCondition || r.itemCondition || '—' }} / {{ r.coverCondition || '—' }}</td>
                <td>
                  <v-text-field v-model.number="r.suggestedPrice" dense hide-details outlined type="number" prefix="₹"
                    placeholder="Enter price" style="max-width: 120px;"></v-text-field>
                </td>
                <td>
                  <v-checkbox v-model="r.apply" dense hide-details class="mt-0"></v-checkbox>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn text @click="autoPriceDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="applyAutoPrices" :loading="applyingPrices"
            :disabled="!autoPriceResults.some(r => r.apply && r.suggestedPrice)">
            <v-icon left small>mdi-check-all</v-icon>
            Apply Selected Prices
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unpriced Items Pricing Dialog -->
    <v-dialog v-model="unpricedDialog" max-width="700" scrollable persistent>
      <v-card>
        <v-card-title class="d-flex align-center warning white--text">
          <v-icon left dark>mdi-currency-usd-off</v-icon>
          Set Prices Before Publishing
          <v-spacer></v-spacer>
          <v-btn icon dark @click="unpricedDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="info" dense text class="mb-4 text-caption">
            {{ unpricedItems.length }} listing(s) don't have a price. Enter prices below to include them in publishing.
          </v-alert>

          <v-simple-table dense>
            <thead>
              <tr>
                <th>Image</th>
                <th>SKU</th>
                <th>Title</th>
                <th>Format</th>
                <th>Condition</th>
                <th>Price (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in unpricedItems" :key="item._id">
                <td>
                  <v-avatar size="36" rounded class="my-1">
                    <v-img v-if="item.images && item.images.length"
                      :src="getImageUrl(item.images[0])" contain></v-img>
                    <v-icon v-else small>mdi-image-off</v-icon>
                  </v-avatar>
                </td>
                <td class="text-caption font-weight-medium">CRC{{ item.sku }}</td>
                <td>
                  <div class="text-caption font-weight-medium text-truncate" style="max-width: 180px;">{{ item.title }}</div>
                  <div class="text-caption grey--text">{{ item.artist }}</div>
                </td>
                <td class="text-caption">{{ item.format || '—' }}</td>
                <td class="text-caption">{{ item.recordCondition || item.itemCondition || '—' }} / {{ item.coverCondition || '—' }}</td>
                <td>
                  <v-text-field v-model.number="item._tempPrice" dense hide-details outlined type="number" prefix="₹"
                    placeholder="Enter price" style="max-width: 130px;"></v-text-field>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn text @click="unpricedDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="warning" outlined @click="skipUnpricedAndPublish">
            <v-icon left small>mdi-debug-step-over</v-icon>
            Skip Unpriced & Publish Rest
          </v-btn>
          <v-btn color="success" @click="saveUnpricedAndPublish" :loading="savingUnpriced"
            :disabled="!unpricedItems.some(i => i._tempPrice > 0)">
            <v-icon left small>mdi-content-save-check</v-icon>
            Save Prices & Publish All
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Publish Confirm -->
    <v-dialog v-model="bulkPublishDialog" max-width="450">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="success">mdi-publish</v-icon>
          Confirm Bulk Publish
        </v-card-title>
        <v-card-text>
          <p>You are about to publish <strong>{{ publishReadyCount }}</strong> listing(s) to WooCommerce.</p>
          <p>This will upload images and create products on the live store. Continue?</p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="bulkPublishDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" @click="bulkPublish" :loading="bulkPublishing">
            <v-icon left small>mdi-publish</v-icon>
            Publish Now
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Overlay for publishing -->
    <v-overlay :value="publishOverlay">
      <div class="text-center">
        <v-progress-circular indeterminate size="64" color="white"></v-progress-circular>
        <div class="white--text mt-4 text-h6">{{ publishOverlayText }}</div>
      </div>
    </v-overlay>
  </v-container>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "Publishing",
  data() {
    return {
      ENDPOINT: process.env.VUE_APP_ENDPOINT,
      listings: [],
      selectedListings: [],
      loading: false,
      totalCount: 0,
      searchQuery: "",
      searchTimeout: null,
      tableOptions: { page: 1, itemsPerPage: 20 },
      headers: [
        { text: "Image", value: "images", sortable: false, width: 70 },
        { text: "SKU", value: "sku", width: 130 },
        { text: "Title / Artist", value: "title" },
        { text: "Format", value: "format", width: 100 },
        { text: "Condition", value: "condition", sortable: false, width: 130 },
        { text: "Price", value: "listPrice", width: 100 },
        { text: "Listed On", value: "listedOn", width: 120 },
        { text: "Actions", value: "actions", sortable: false, width: 110 },
      ],

      // Pricing dialog
      pricingDialog: false,
      dialogListing: null,
      dialogPrice: null,
      similarProducts: [],
      loadingSimilar: false,
      savingPrice: false,
      publishing: false,

      // Inline price editing
      editingPriceId: null,
      editingPriceValue: null,

      // Auto pricing
      autoPricing: false,
      autoPriceDialog: false,
      autoPriceResults: [],
      applyingPrices: false,

      // Unpriced items dialog
      unpricedDialog: false,
      unpricedItems: [],
      savingUnpriced: false,

      // Bulk publish
      bulkPublishDialog: false,
      bulkPublishing: false,
      publishOverlay: false,
      publishOverlayText: "",
    };
  },
  computed: {
    averageSimilarPrice() {
      const prices = this.similarProducts.map((s) => s.listPrice).filter((p) => p && p > 0);
      if (!prices.length) return 0;
      return Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
    },
    unpricedInSelection() {
      return this.selectedListings.filter((l) => !l.listPrice);
    },
    publishReadyCount() {
      return this.selectedListings.filter((l) => l.listPrice > 0).length;
    },
  },
  watch: {
    tableOptions: {
      handler() {
        this.fetchDrafts();
      },
      deep: true,
    },
  },
  created() {
    this.fetchDrafts();
  },
  methods: {
    getImageUrl(image) {
      if (!image) return '';
      if (image.startsWith('http://') || image.startsWith('https://')) return image;
      return `${this.ENDPOINT}/uploads/compressed/${image}`;
    },
    formatDate(date) {
      return date ? moment(date).format("DD MMM YYYY") : "—";
    },
    // --- Inline Price Edit ---
    startInlineEdit(item) {
      this.editingPriceId = item._id;
      this.editingPriceValue = item.listPrice || null;
    },

    async saveInlinePrice(item) {
      if (this.editingPriceId !== item._id) return;
      const newPrice = this.editingPriceValue;
      this.editingPriceId = null;

      if (!newPrice || newPrice <= 0) return;
      if (newPrice === item.listPrice) return;

      try {
        await axios.put(`${this.ENDPOINT}/listings/${item._id}`, {
          listPrice: newPrice,
          sellingPrice: newPrice,
        });
        const idx = this.listings.findIndex((l) => l._id === item._id);
        if (idx >= 0) {
          this.$set(this.listings[idx], 'listPrice', newPrice);
          this.$set(this.listings[idx], 'sellingPrice', newPrice);
        }
      } catch (error) {
        console.error('Error saving inline price:', error);
        this.$swal('Error', 'Failed to save price', 'error');
      }
    },
    debouncedFetch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.tableOptions.page = 1;
        this.fetchDrafts();
      }, 400);
    },

    async fetchDrafts() {
      this.loading = true;
      try {
        const { page, itemsPerPage } = this.tableOptions;
        const params = {
          page: page - 1,
          limit: itemsPerPage,
        };
        if (this.searchQuery) params.search = this.searchQuery;

        const resp = await axios.get(`${this.ENDPOINT}/listings/drafts`, { params });
        this.listings = resp.data.listings;
        this.totalCount = resp.data.totalCount;
      } catch (error) {
        console.error("Error fetching drafts:", error);
        this.$swal("Error", "Failed to fetch draft listings", "error");
      } finally {
        this.loading = false;
      }
    },

    // --- Pricing Dialog ---
    async openPricingDialog(item) {
      this.dialogListing = item;
      this.dialogPrice = item.listPrice || item.sellingPrice || null;
      this.similarProducts = [];
      this.pricingDialog = true;
      this.fetchSimilar();
    },

    async fetchSimilar() {
      if (!this.dialogListing) return;
      this.loadingSimilar = true;
      try {
        const resp = await axios.post(`${this.ENDPOINT}/listings/similar`, {
          artist: this.dialogListing.artist,
          title: this.dialogListing.title,
          album: this.dialogListing.album || null,
          format: this.dialogListing.format || null,
          listingId: this.dialogListing._id,
          recordCondition: this.dialogListing.recordCondition || null,
          itemCondition: this.dialogListing.itemCondition || null,
        });
        this.similarProducts = resp.data.similar;
      } catch (error) {
        console.error("Error fetching similar:", error);
      } finally {
        this.loadingSimilar = false;
      }
    },

    async savePriceOnly() {
      if (!this.dialogPrice || !this.dialogListing) return;
      this.savingPrice = true;
      try {
        await axios.put(`${this.ENDPOINT}/listings/${this.dialogListing._id}`, {
          listPrice: this.dialogPrice,
          sellingPrice: this.dialogPrice,
        });

        // Update local data
        const idx = this.listings.findIndex((l) => l._id === this.dialogListing._id);
        if (idx >= 0) {
          this.$set(this.listings[idx], "listPrice", this.dialogPrice);
          this.$set(this.listings[idx], "sellingPrice", this.dialogPrice);
        }

        this.$swal("Saved", "Price updated. Listing kept as draft.", "success");
        this.pricingDialog = false;
      } catch (error) {
        console.error("Error saving price:", error);
        this.$swal("Error", "Failed to save price", "error");
      } finally {
        this.savingPrice = false;
      }
    },

    async savePriceAndPublish() {
      if (!this.dialogPrice || !this.dialogListing) return;
      this.publishing = true;
      try {
        // First save the price
        await axios.put(`${this.ENDPOINT}/listings/${this.dialogListing._id}`, {
          listPrice: this.dialogPrice,
          sellingPrice: this.dialogPrice,
        });

        // Then publish
        await axios.post(`${this.ENDPOINT}/listings/${this.dialogListing._id}/post`);

        // Remove from local list
        this.listings = this.listings.filter((l) => l._id !== this.dialogListing._id);
        this.totalCount -= 1;

        this.$swal("Published!", "Listing published to WooCommerce successfully.", "success");
        this.pricingDialog = false;
      } catch (error) {
        console.error("Error publishing:", error);
        this.$swal("Error", "Failed to publish: " + (error.response?.data?.error?.message || error.message), "error");
      } finally {
        this.publishing = false;
      }
    },

    async publishSingle(item) {
      if (!item.listPrice) {
        this.openPricingDialog(item);
        return;
      }

      const result = await this.$swal({
        title: "Publish this listing?",
        text: `"${item.title}" at ₹${item.listPrice}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Publish",
        confirmButtonColor: "#4CAF50",
      });

      if (!result.isConfirmed) return;

      this.$set(item, "_publishing", true);
      try {
        await axios.post(`${this.ENDPOINT}/listings/${item._id}/post`);
        this.listings = this.listings.filter((l) => l._id !== item._id);
        this.totalCount -= 1;
        this.$swal("Published!", "Listing published successfully.", "success");
      } catch (error) {
        console.error("Publish error:", error);
        this.$swal("Error", "Failed to publish", "error");
      } finally {
        this.$set(item, "_publishing", false);
      }
    },

    // --- Auto Pricing ---
    async autoPrice() {
      if (!this.selectedListings.length) return;
      this.autoPricing = true;
      try {
        const ids = this.selectedListings.map((l) => l._id);
        const resp = await axios.post(`${this.ENDPOINT}/listings/auto-price`, { listingIds: ids });
        const allResults = resp.data.results;

        // Separate: items with matches get auto-applied, items without matches go to popup
        const withMatches = allResults.filter((r) => r.suggestedPrice && r.matchCount > 0);
        const withoutMatches = allResults.filter((r) => !r.suggestedPrice || r.matchCount === 0);

        // Auto-apply prices for items with matches
        if (withMatches.length) {
          const updates = withMatches.map((r) => ({
            _id: r._id,
            listPrice: r.suggestedPrice,
          }));
          await axios.post(`${this.ENDPOINT}/listings/bulk-update-price`, { updates });

          // Update local data
          for (const u of updates) {
            const idx = this.listings.findIndex((l) => l._id === u._id);
            if (idx >= 0) {
              this.$set(this.listings[idx], 'listPrice', u.listPrice);
              this.$set(this.listings[idx], 'sellingPrice', u.listPrice);
            }
          }
        }

        if (withoutMatches.length === 0) {
          // All items had matches and got auto-priced
          this.$swal('Auto Priced', `Prices applied to ${withMatches.length} listing(s) based on similar products.`, 'success');
          this.selectedListings = [];
        } else {
          // Show popup only for items without matches
          this.autoPriceResults = withoutMatches.map((r) => ({
            ...r,
            suggestedPrice: r.currentPrice || null,
            apply: false,
          }));
          this.autoPriceDialog = true;

          if (withMatches.length) {
            // Notify about auto-applied ones
            this.$swal('Partial Auto Price', `${withMatches.length} listing(s) priced automatically. ${withoutMatches.length} need manual pricing.`, 'info');
          }
        }
      } catch (error) {
        console.error('Auto price error:', error);
        this.$swal('Error', 'Auto pricing failed', 'error');
      } finally {
        this.autoPricing = false;
      }
    },

    async applyAutoPrices() {
      const toApply = this.autoPriceResults.filter((r) => r.apply && r.suggestedPrice);
      if (!toApply.length) return;
      this.applyingPrices = true;
      try {
        const updates = toApply.map((r) => ({
          _id: r._id,
          listPrice: r.suggestedPrice,
        }));
        await axios.post(`${this.ENDPOINT}/listings/bulk-update-price`, { updates });

        // Update local data
        for (const u of updates) {
          const idx = this.listings.findIndex((l) => l._id === u._id);
          if (idx >= 0) {
            this.$set(this.listings[idx], "listPrice", u.listPrice);
            this.$set(this.listings[idx], "sellingPrice", u.listPrice);
          }
        }

        this.$swal("Done", `Prices applied to ${updates.length} listing(s).`, "success");
        this.autoPriceDialog = false;
        this.selectedListings = [];
      } catch (error) {
        console.error("Apply prices error:", error);
        this.$swal("Error", "Failed to apply prices", "error");
      } finally {
        this.applyingPrices = false;
      }
    },

    // --- Bulk Publish ---
    bulkPublishConfirm() {
      const unpriced = this.selectedListings.filter((l) => !l.listPrice || l.listPrice <= 0);
      if (unpriced.length > 0) {
        // Show pricing popup for unpriced items
        this.unpricedItems = unpriced.map((item) => {
          this.$set(item, '_tempPrice', null);
          return item;
        });
        this.unpricedDialog = true;
      } else {
        // All have prices, go straight to confirm
        this.bulkPublishDialog = true;
      }
    },

    async saveUnpricedAndPublish() {
      this.savingUnpriced = true;
      try {
        // Save prices for items that have a price entered
        const toUpdate = this.unpricedItems.filter((i) => i._tempPrice > 0);
        if (toUpdate.length) {
          const updates = toUpdate.map((i) => ({
            _id: i._id,
            listPrice: i._tempPrice,
          }));
          await axios.post(`${this.ENDPOINT}/listings/bulk-update-price`, { updates });

          // Update local data
          for (const u of updates) {
            const idx = this.listings.findIndex((l) => l._id === u._id);
            if (idx >= 0) {
              this.$set(this.listings[idx], 'listPrice', u.listPrice);
              this.$set(this.listings[idx], 'sellingPrice', u.listPrice);
            }
            // Also update in selectedListings
            const sel = this.selectedListings.find((l) => l._id === u._id);
            if (sel) {
              sel.listPrice = u.listPrice;
              sel.sellingPrice = u.listPrice;
            }
          }
        }

        this.unpricedDialog = false;
        // Now show the publish confirmation
        this.bulkPublishDialog = true;
      } catch (error) {
        console.error('Error saving unpriced items:', error);
        this.$swal('Error', 'Failed to save prices', 'error');
      } finally {
        this.savingUnpriced = false;
      }
    },

    skipUnpricedAndPublish() {
      this.unpricedDialog = false;
      // Go straight to publish confirm — only priced items will be published
      this.bulkPublishDialog = true;
    },

    async bulkPublish() {
      const toPublish = this.selectedListings.filter((l) => l.listPrice > 0);
      if (!toPublish.length) {
        this.$swal("Warning", "No listings with valid prices to publish.", "warning");
        return;
      }

      this.bulkPublishDialog = false;
      this.publishOverlay = true;

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < toPublish.length; i++) {
        const item = toPublish[i];
        this.publishOverlayText = `Publishing ${i + 1} of ${toPublish.length}: ${item.title}`;
        try {
          await axios.post(`${this.ENDPOINT}/listings/${item._id}/post`);
          this.listings = this.listings.filter((l) => l._id !== item._id);
          this.totalCount -= 1;
          successCount++;
        } catch (error) {
          console.error(`Failed to publish ${item.title}:`, error);
          failCount++;
        }
      }

      this.publishOverlay = false;
      this.selectedListings = [];

      const msg = failCount
        ? `Published ${successCount}, failed ${failCount}.`
        : `Successfully published ${successCount} listing(s)!`;
      this.$swal(failCount ? "Partial Success" : "Done!", msg, failCount ? "warning" : "success");
    },
  },
};
</script>

<style scoped>
.draft-table >>> tbody tr {
  cursor: pointer;
}
.draft-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}
.similar-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.inline-price-input >>> .v-input__slot {
  min-height: 28px !important;
  padding: 0 6px !important;
  border: 1px solid #1976d2 !important;
  border-radius: 4px;
}
.inline-price-input >>> input {
  font-size: 12px !important;
  padding: 2px 0 !important;
  -moz-appearance: textfield;
}
.inline-price-input >>> input::-webkit-outer-spin-button,
.inline-price-input >>> input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.inline-price-input >>> .v-text-field__prefix {
  font-size: 12px !important;
}
</style>
